/**
 * @aiPurpose plays a notification sound by sound-key (#1073). The
 *   sound files live under `apps/web/public/audio/notifications/`;
 *   the key is the file name from the shared closed allowlist
 *   `NotificationSoundKey`.
 * @aiInvariant browser autoplay policy may reject `.play()` until the
 *   user has interacted with the page. The promise is swallowed
 *   (returns `false`) so a rejected play never propagates as an
 *   unhandled error or surfaces in the UI.
 */
import type { NotificationSoundKey } from '@magimp/shared';

export type AudioFactory = (url: string) => HTMLAudioElement;

export function notificationSoundUrl(key: NotificationSoundKey): string {
  return `/audio/notifications/${key}`;
}

const defaultAudioFactory: AudioFactory = (url) => new Audio(url);

export async function playNotificationSound(
  key: NotificationSoundKey,
  factory: AudioFactory = defaultAudioFactory,
): Promise<boolean> {
  try {
    const audio = factory(notificationSoundUrl(key));
    const result = audio.play();
    // WHY: HTMLAudioElement.play in legacy Safari returned `undefined`
    // instead of a Promise; the modern contract is a Promise but the
    // defensive branch keeps us safe in tests that mock the element.
    if (result && typeof result.then === 'function') {
      await result;
    }
    return true;
  } catch {
    return false;
  }
}
